import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { Granularity, NewVsReturningPoint } from '../../api/types';

interface Props {
  points: NewVsReturningPoint[];
  loading?: boolean;
  granularity?: Granularity;
}

/**
 * Format the bucket label for the x-axis. Same shape rules as
 * `AdoptionLineChart` — hour buckets read as `MM-DD HH:00`, day
 * buckets as `MM-DD`, month buckets stay as `YYYY-MM`.
 */
function formatBucket(v: string, granularity: Granularity): string {
  if (granularity === 'hour') return `${v.slice(5, 10)} ${v.slice(11, 13)}:00`;
  if (granularity === 'month') return v;
  return v.slice(5);
}

const COLOR_NEW = '#10b981'; // emerald 500 — onboarding
const COLOR_RETURNING = '#6366f1'; // indigo 500 — retained

export function NewVsReturningUsersChart({
  points,
  loading,
  granularity = 'day',
}: Props): JSX.Element {
  const option: EChartsOption = {
    grid: { left: 48, right: 24, top: 56, bottom: 36 },
    legend: {
      top: 8,
      icon: 'roundRect',
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { fontSize: 12 },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
    },
    xAxis: {
      type: 'category',
      data: points.map((p) => p.date),
      axisLabel: { formatter: (v: string) => formatBucket(v, granularity) },
    },
    yAxis: {
      type: 'value',
      name: 'Users',
      nameLocation: 'middle',
      nameGap: 36,
      minInterval: 1,
    },
    series: [
      {
        name: 'New',
        type: 'bar',
        stack: 'users',
        barMaxWidth: 36,
        itemStyle: { color: COLOR_NEW },
        data: points.map((p) => p.new),
      },
      {
        name: 'Returning',
        type: 'bar',
        stack: 'users',
        barMaxWidth: 36,
        itemStyle: { color: COLOR_RETURNING },
        data: points.map((p) => p.returning),
      },
    ],
  };
  return (
    <ReactECharts
      option={option}
      style={{ height: 300, width: '100%' }}
      showLoading={loading}
      notMerge
      lazyUpdate
    />
  );
}
